import React, { useEffect, useMemo, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import SwipeableDrawer from '@material-ui/core/SwipeableDrawer';
import Dialog from '@material-ui/core/Dialog';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import axios from 'axios';
import { appUrl } from '../../../../../../utils/config';
import BatchProgressBar from '../../sasl/batch/updateBatch/BatchProgressBar';
import QuickView from '../../sasl/common/drawer/QuickView';
import CommonDigitizedDocument from './CommonDigitizedDocument';
import StatusPill from './StatusPill';
import { formatDate } from '../helpers/dateUtils';

// ─── Constants ────────────────────────────────────────────────────────────────

const TIMELINE_STEPS = [
  { id: 62, label: 'Shipment Authorized',      sequence: 1 },
  { id: 63, label: 'Shipment Scheduled',       sequence: 2 },
  { id: 64, label: 'Shipped',                  sequence: 3 },
  { id: 65, label: 'Proforma Requested',       sequence: 4 },
  { id: 66, label: 'Proforma Uploaded',        sequence: 5 },
  { id: 67, label: 'Proforma Digitalized',     sequence: 6 },
  { id: 68, label: 'Proforma Sent',            sequence: 7 },
  { id: 59, label: 'Goods Received',           sequence: 8 },
  { id: 69, label: 'Consignee GRN Generated',  sequence: 9 },
];

// Status IDs at which each cumulative event becomes visible
const SCHEDULED_STATUS_IDS  = new Set([63, 64, 65, 66, 67, 68, 59, 69]); // Shipment Scheduled and beyond
const DISPATCHED_STATUS_IDS = new Set([64, 65, 66, 67, 68, 59, 69]);      // Shipped and beyond
const DELIVERED_STATUS_IDS  = new Set([59, 69]);                           // Goods Received, Consignee GRN Generated

const getShipmentEvents = (statusId) => {
  const events = [];
  if (DELIVERED_STATUS_IDS.has(statusId)) {
    events.push({ event: 'Goods delivered at Consignee',      date: '01-JUL-2026', time: '10:11:07' });
  }
  if (DISPATCHED_STATUS_IDS.has(statusId)) {
    events.push({ event: 'The shipment has been dispatched.', date: '29-JUN-2026', time: '08:20:20' });
  }
  if (SCHEDULED_STATUS_IDS.has(statusId)) {
    events.push({ event: 'Shipment has been scheduled.',      date: '28-JUN-2026', time: '12:12:04' });
  }
  return events;
};

const MOCK_SENSOR_DATA = [
  { label: 'Temperature', value: '6.1 °C', timestamp: '14:12:58' },
  { label: 'GPS', value: '23.0190 °N, 72.5067 °E', timestamp: '14:10:15' },
  { label: 'Vibration', value: '8 Hz', timestamp: '13:41:36' },
];

// SVG chart config
const C = {
  svgW: 660, svgH: 280,
  ml: 52, mr: 8, mt: 14, mb: 44,
  minT: 0, maxT: 15,
  startH: 7, endH: 15,
  upper: 8, lower: 2,
};
C.plotW = C.svgW - C.ml - C.mr;
C.plotH = C.svgH - C.mt - C.mb;

const tx = (h) => C.ml + ((h - C.startH) / (C.endH - C.startH)) * C.plotW;
const ty = (t) => C.mt + ((C.maxT - t) / (C.maxT - C.minT)) * C.plotH;

const TEMP_DATA = [
  { h: 7.00, t: 5.2 }, { h: 7.25, t: 4.9 }, { h: 7.50, t: 4.8 },
  { h: 7.75, t: 4.7 }, { h: 8.00, t: 4.9 }, { h: 8.25, t: 4.9 },
  { h: 8.50, t: 5.0 }, { h: 8.75, t: 4.9 }, { h: 9.00, t: 5.0 },
  { h: 9.25, t: 5.1 }, { h: 9.50, t: 5.0 }, { h: 9.75, t: 5.1 },
  { h: 10.00, t: 5.1 }, { h: 10.25, t: 5.0 }, { h: 10.50, t: 5.1 },
  { h: 10.75, t: 5.2 }, { h: 11.00, t: 5.2 }, { h: 11.25, t: 5.1 },
  { h: 11.50, t: 5.2 }, { h: 11.75, t: 5.2 }, { h: 12.00, t: 5.3 },
  { h: 12.25, t: 5.2 }, { h: 12.50, t: 5.3 }, { h: 12.75, t: 5.3 },
  { h: 13.00, t: 5.4 }, { h: 13.25, t: 5.3 }, { h: 13.50, t: 5.4 },
  { h: 13.75, t: 5.5 }, { h: 14.00, t: 5.6 }, { h: 14.25, t: 5.7 },
  { h: 14.50, t: 5.8 }, { h: 14.75, t: 5.9 }, { h: 15.00, t: 5.9 },
];
const TEMP_PATH = TEMP_DATA.map((d, i) =>
  `${i === 0 ? 'M' : 'L'}${tx(d.h).toFixed(1)},${ty(d.t).toFixed(1)}`
).join(' ');

const Y_TICKS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15];
const X_LABELS = ['07:00:00', '08:00:00', '09:00:00', '10:00:00', '11:00:00', '12:00:00', '13:00:00', '14:00:00', '15:00:00'];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const buildProgressBar = (statusId) => {
  const currentIdx = TIMELINE_STEPS.findIndex((s) => s.id === statusId);
  return {
    statusSequence: TIMELINE_STEPS.map((step, idx) => {
      let state = 'future';
      if (currentIdx >= 0) {
        if (idx < currentIdx) state = 'completed';
        else if (idx === currentIdx) state = 'current';
        else if (idx === currentIdx + 1) state = 'upcoming';
      }
      return { statusID: step.id, statusName: step.label, sequence: step.sequence, state };
    }),
    pendingRequirements: [],
  };
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  pageRoot: {
    height: '100%',
    overflowY: 'auto',
    backgroundColor: '#FFFFFF',
    display: 'flex',
    flexDirection: 'column',
    outline: 'none',
  },
  appBar: {
    backgroundColor: '#FFFFFF',
    color: '#000000',
    boxShadow: 'none',
    borderBottom: '1px solid #B4B4B4',
  },
  backBtn: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    gap: 2,
    '&:hover': { borderBottom: '2px solid black' },
  },
  backLabel: {
    fontWeight: 500,
    [theme.breakpoints.down('sm')]: { display: 'none' },
  },
  pageTitle: {
    fontWeight: 500,
    textAlign: 'left',
    [theme.breakpoints.down('xs')]: { fontSize: '0.875rem' },
  },
  digitizedBtn: {
    backgroundColor: '#1565C0',
    color: '#FFFFFF',
    textTransform: 'none',
    fontSize: '0.78rem',
    whiteSpace: 'nowrap',
    padding: '5px 12px',
    '&:hover': { backgroundColor: '#0D47A1' },
  },
  bodyContent: { paddingTop: '1rem', flex: 1 },
  innerPadding: { padding: '0 1rem 2rem 1rem' },
  idLabel: {
    fontSize: '0.72rem',
    fontWeight: 600,
    color: '#666',
    marginBottom: 2,
  },
  idValue: { fontSize: '0.95rem', fontWeight: 500 },
  statusBox: {
    backgroundColor: '#FAFAFA',
    border: '1px solid #D0D0D0',
    borderRadius: 4,
    padding: '1rem',
    marginBottom: 20,
  },
  statusLabel: { fontSize: '1rem', fontWeight: 700 },
  statusValue: { fontSize: '1rem', paddingTop: '0.5rem' },
  statusValueRed: {
    fontSize: '1rem',
    paddingTop: '0.5rem',
    color: '#E53935',
  },
  hintText: {
    fontSize: '0.875rem',
    fontStyle: 'italic',
    color: '#666',
  },
  sectionTitle: {
    fontWeight: 500,
    fontSize: '1.5rem',
    textAlign: 'left',
  },
  sectionBorder: {
    border: '2px solid #707070',
    display: 'flex',
    width: '1.6rem',
    backgroundColor: 'grey',
    marginBottom: '1rem',
  },
  sectionWrapper: { marginTop: '1.25rem', marginBottom: '0.5rem' },
  batchLink: {
    color: '#1565C0',
    textDecoration: 'underline',
    fontSize: '1rem',
    cursor: 'pointer',
  },
  batchRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginBottom: 10,
    cursor: 'pointer',
    width: 'fit-content',
  },
  tableContainer: {
    marginTop: 0,
    boxShadow: 'none',
    borderRadius: 0,
    border: '1px solid #E0E0E0',
    backgroundColor: '#fff',
    maxWidth: 900,
  },
  tableHeader: { backgroundColor: '#F5F5F5' },
  headerCell: {
    fontWeight: 'bold',
    fontSize: 14,
    color: '#333',
    padding: '8px 10px',
    borderBottom: '1px solid #E0E0E0',
    whiteSpace: 'nowrap',
  },
  tableRow: { backgroundColor: '#fff' },
  tableCell: {
    fontSize: 14,
    color: '#333',
    padding: '6px 10px',
    borderBottom: '1px solid #E0E0E0',
    whiteSpace: 'nowrap',
  },
  boldCell: {
    fontWeight: 'bold',
    fontSize: 14,
    color: '#333',
    padding: '6px 10px',
    borderBottom: '1px solid #E0E0E0',
    whiteSpace: 'nowrap',
  },
  linkCell: {
    fontSize: 14,
    color: '#1565C0',
    textDecoration: 'underline',
    padding: '6px 10px',
    borderBottom: '1px solid #E0E0E0',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  eventsDot: {
    width: 14,
    height: 14,
    borderRadius: '50%',
    backgroundColor: '#E64A19',
    flexShrink: 0,
    marginTop: 3,
  },
  eventsLine: {
    width: 2,
    flex: 1,
    minHeight: 20,
    borderLeft: '2px dashed #E64A19',
  },
  eventName: { fontSize: '0.95rem', fontWeight: 500 },
  eventMeta: { fontSize: '0.82rem', color: '#555' },
  fileCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 12px',
    border: '1px solid #E0E0E0',
    borderRadius: 4,
    marginBottom: 8,
    backgroundColor: '#FAFAFA',
    cursor: 'pointer',
    maxWidth: 700,
    '&:hover': { backgroundColor: '#F0F0F0' },
  },
  chartOuter: {
    border: '1px solid #E0E0E0',
    borderRadius: 4,
    padding: 16,
    maxWidth: 860,
  },
  toggleActive: {
    backgroundColor: '#000',
    color: '#fff',
    borderRadius: 20,
    padding: '4px 14px',
    fontSize: '0.78rem',
    fontWeight: 600,
    textTransform: 'none',
    '&:hover': { backgroundColor: '#222' },
  },
  toggleInactive: {
    backgroundColor: '#fff',
    color: '#333',
    borderRadius: 20,
    padding: '4px 14px',
    fontSize: '0.78rem',
    fontWeight: 600,
    textTransform: 'none',
    border: '1px solid #ccc',
    '&:hover': { backgroundColor: '#f5f5f5' },
  },
  legendLine: {
    width: 24,
    borderTop: '2px dashed #1565C0',
    marginRight: 6,
  },
  viewDeviationsBtn: {
    backgroundColor: '#E64A19',
    color: '#fff',
    borderRadius: 20,
    padding: '6px 16px',
    fontSize: '0.78rem',
    fontWeight: 600,
    textTransform: 'none',
    '&:hover': { backgroundColor: '#BF360C' },
  },
  drawerPaper: { width: '100vw' },
}));

// ─── ShipmentProfileChart (decorative) ────────────────────────────────────────

const METRICS = ['Temperature', 'GPS', 'Vibration'];

const ShipmentProfileChart = ({ classes }) => {
  const [active, setActive] = useState('Temperature');
  const upperY = ty(C.upper);
  const lowerY = ty(C.lower);

  return (
    <Box className={classes.chartOuter}>
      <Box style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 8 }}>
        {METRICS.map((m) => (
          <Button
            key={m}
            className={active === m ? classes.toggleActive : classes.toggleInactive}
            onClick={() => setActive(m)}
            disableElevation
            variant={active === m ? 'contained' : 'outlined'}
          >
            {m}
          </Button>
        ))}
      </Box>

      <Box style={{ overflowX: 'auto' }}>
        <svg viewBox={`0 0 ${C.svgW} ${C.svgH}`} style={{ width: '100%', minWidth: 380, display: 'block' }}>
          {Y_TICKS.map((t) => (
            <line key={t} x1={C.ml} x2={C.ml + C.plotW} y1={ty(t)} y2={ty(t)} stroke="#E0E0E0" strokeWidth={0.5} />
          ))}
          {Y_TICKS.map((t) => (
            <text key={t} x={C.ml - 6} y={ty(t) + 4} textAnchor="end" fontSize={9} fill="#666">{t}</text>
          ))}
          <text
            x={10}
            y={C.mt + C.plotH / 2}
            textAnchor="middle"
            fontSize={9}
            fill="#555"
            transform={`rotate(-90,10,${C.mt + C.plotH / 2})`}
          >
            Temperature (°C)
          </text>
          {X_LABELS.map((lbl, i) => (
            <text key={lbl} x={tx(7 + i)} y={C.mt + C.plotH + 14} textAnchor="middle" fontSize={8} fill="#666">{lbl}</text>
          ))}
          <text x={C.ml + C.plotW / 2} y={C.svgH - 4} textAnchor="middle" fontSize={9} fill="#555">Time (Hr)</text>
          <line x1={C.ml} y1={C.mt} x2={C.ml} y2={C.mt + C.plotH} stroke="#999" strokeWidth={1} />
          <line x1={C.ml} y1={C.mt + C.plotH} x2={C.ml + C.plotW} y2={C.mt + C.plotH} stroke="#999" strokeWidth={1} />
          <line x1={C.ml} x2={C.ml + C.plotW} y1={upperY} y2={upperY} stroke="#1565C0" strokeWidth={1.5} strokeDasharray="6,4" />
          <line x1={C.ml} x2={C.ml + C.plotW} y1={lowerY} y2={lowerY} stroke="#1565C0" strokeWidth={1.5} strokeDasharray="6,4" />
          <path d={TEMP_PATH} fill="none" stroke="#E64A19" strokeWidth={2} strokeLinejoin="round" />
        </svg>
      </Box>

      <Box style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 20, marginTop: 8, flexWrap: 'wrap' }}>
        <Box style={{ display: 'flex', alignItems: 'center' }}>
          <Box className={classes.legendLine} />
          <Typography style={{ fontSize: '0.78rem' }}>Upper Limit = {C.upper} °C</Typography>
        </Box>
        <Box style={{ display: 'flex', alignItems: 'center' }}>
          <Box className={classes.legendLine} />
          <Typography style={{ fontSize: '0.78rem' }}>Lower Limit = {C.lower} °C</Typography>
        </Box>
        <Button className={classes.viewDeviationsBtn} disableElevation>
          View Deviations &gt;
        </Button>
      </Box>
    </Box>
  );
};

const SectionHeader = ({ title, classes }) => (
  <Box className={classes.sectionWrapper}>
    <Typography variant="h4" className={classes.sectionTitle}>{title}</Typography>
    <span className={classes.sectionBorder} />
  </Box>
);

// ─── ShipmentDetailsPage ───────────────────────────────────────────────────────

const ShipmentDetailsPage = ({ row, onBack }) => {
  const classes = useStyles();
  const statusId = row.statusID || row.statusId;
  const [batchDrawer, setBatchDrawer] = useState({ open: false, batch: null });
  const [showDigitized, setShowDigitized] = useState(false);
  const scrollRef = useRef(null);

  // The scroll container is a plain <div> rendered inside a fullScreen Dialog,
  // which isn't keyboard-focusable by default — so arrow keys / PageUp-Down /
  // Space don't scroll it. Make it focusable (tabIndex below) and focus it on
  // mount (after the Dialog's own focus trap settles) to enable keyboard scroll.
  useEffect(() => {
    const t = setTimeout(() => {
      if (scrollRef.current) scrollRef.current.focus();
    }, 0);
    return () => clearTimeout(t);
  }, []);

  const currentIdx = useMemo(
    () => TIMELINE_STEPS.findIndex((s) => s.id === statusId),
    [statusId],
  );
  const currentStep = currentIdx >= 0 ? TIMELINE_STEPS[currentIdx] : null;

  const [progressBarData, setProgressBarData] = useState(null);

  useEffect(() => {
    const id = row.collabrixFGBatchShipmentID;
    if (!id) return;
    const base = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}`;
    axios
      .post(`${base}/fetchShipmentProgressBar`, { collabrixFGBatchShipmentID: id })
      .then((res) => {
        const pb = res?.data?.data?.progressBar;
        if (pb) setProgressBarData(pb);
      })
      .catch(() => {});
  }, [row.collabrixFGBatchShipmentID]);

  const nextStatusLabel = useMemo(() => {
    if (progressBarData) {
      const upcoming = progressBarData.statusSequence?.find((s) => s.state === 'upcoming');
      return upcoming?.statusName || null;
    }
    if (currentIdx >= 0 && currentIdx < TIMELINE_STEPS.length - 1) {
      return TIMELINE_STEPS[currentIdx + 1].label;
    }
    return null;
  }, [progressBarData, currentIdx]);

  const batchesList = useMemo(
    () => (Array.isArray(row.batchesList) ? row.batchesList : []),
    [row.batchesList],
  );
  const shipmentEvents = useMemo(() => getShipmentEvents(statusId), [statusId]);
  const customFilesList = Array.isArray(row.customFilesList) ? row.customFilesList : [];

  // GRN data comes from each batch's consigneeGRNNumber/consigneeGRNGeneratedDate
  const grnBatches = useMemo(
    () => batchesList.filter((b) => b.vendorBatchNumber),
    [batchesList],
  );

  const progressBar = useMemo(
    () => progressBarData || buildProgressBar(statusId),
    [progressBarData, statusId],
  );
  const hasDigitizedData = Boolean(row.kvpJsonEdited || row.kvpJson);

  if (showDigitized) {
    const file =
      customFilesList.length > 0 ? customFilesList[0] : null;
    const kvp = row.kvpJsonEdited || row.kvpJson || {};
    return (
      <Box style={{ padding: '24px', height: '100%', boxSizing: 'border-box', overflowY: 'auto' }}>
        <CommonDigitizedDocument
          initialData={row.kvpJson || {}}
          digitizedData={
            row.kvpJson
              ? { ...row.kvpJson, documentType: row.kvpJson.document_type }
              : kvp
          }
          batchDetails={row}
          poNumber={row.scheduleAgreementNumber || (batchesList[0] && batchesList[0].scheduleAgreementNumber)}
          batchNumber={batchesList[0] && batchesList[0].vendorBatchNumber}
          schrockenCustomFileID={file ? file.schrockenCustomFileID : null}
          onClose={() => setShowDigitized(false)}
        />
      </Box>
    );
  }

  return (
    <div className={classes.pageRoot} ref={scrollRef} tabIndex={0}>
      {/* ── AppBar ── */}
      <AppBar position="sticky" className={classes.appBar}>
        <Toolbar>
          <Box className={classes.backBtn} onClick={onBack}>
            <ArrowBackIosIcon fontSize="small" />
            <Typography variant="body2" className={classes.backLabel}>Back</Typography>
          </Box>
          <Typography variant="h5" className={classes.pageTitle} style={{ marginLeft: 12 }}>
            Shipment Details
          </Typography>
        </Toolbar>
      </AppBar>

      {/* ── Body ── */}
      <Box className={classes.bodyContent}>
        <Grid container justify="center" alignItems="center">
          <Grid item xs={11} sm={10} md={11}>
            <Box className={classes.innerPadding}>

              {/* IDs row + Digitalized button */}
              <Box style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 20 }}>
                <Box style={{ display: 'flex', gap: 48, flexWrap: 'wrap' }}>
                  {[
                    ['Sponsor Shipment ID', row.sponsorShipmentID],
                    ['FF Schedule ID', row.freightForwarderScheduleID],
                    ['Vendor Shipment ID', row.vendorShipmentID],
                  ].map(([label, val]) => (
                    <Box key={label}>
                      <Typography className={classes.idLabel}>{label}</Typography>
                      <Typography className={classes.idValue}>{val || '-'}</Typography>
                    </Box>
                  ))}
                </Box>
                {hasDigitizedData && (
                  <Button
                    className={classes.digitizedBtn}
                    variant="contained"
                    disableElevation
                    onClick={() => setShowDigitized(true)}
                  >
                    View Digitalized Information
                  </Button>
                )}
              </Box>

              {/* ── Status box ── */}
              <Box className={classes.statusBox}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" className={classes.statusLabel}>Current Status</Typography>
                    <Box style={{ paddingTop: '0.5rem' }}>
                      {currentStep || row.statusDescription
                        ? (
                          <StatusPill
                            statusId={statusId}
                            statusDescription={row.statusDescription || (currentStep && currentStep.label)}
                          />
                        )
                        : <Typography variant="body2" className={classes.statusValue}>-</Typography>
                      }
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" className={classes.statusLabel}>Next Status</Typography>
                    <Typography variant="body2" className={classes.statusValueRed}>
                      {nextStatusLabel || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" className={classes.statusLabel}>Planned Dispatch</Typography>
                    <Typography variant="body2" className={classes.statusValue}>
                      {formatDate(row.plannedDispatchDate) || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" className={classes.statusLabel}>Estimated Arrival</Typography>
                    <Typography variant="body2" className={classes.statusValue}>
                      {formatDate(row.estimatedArrivalDate) || '-'}
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container style={{ paddingTop: '0.5rem' }}>
                  <Grid item xs={12}>
                    <Typography variant="body2" className={classes.hintText}>
                      Hover over the circle to see the status.
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container style={{ paddingTop: '1rem', overflowX: 'auto' }}>
                  <Grid item xs={12}>
                    <BatchProgressBar progressBar={progressBar} />
                  </Grid>
                </Grid>
              </Box>



              <hr style={{ margin: '1.5rem 0', borderColor: '#E0E0E0' }} />

              {/* ── Batch Details ── */}
              <SectionHeader title="Batch Details" classes={classes} />
              {batchesList.length === 0 ? (
                <Typography style={{ fontSize: '1rem', color: '#777' }}>No batches found.</Typography>
              ) : (
                batchesList.map((batch) => (
                  <Box
                    key={batch.schrockenDeliveryScheduleBatchID}
                    className={classes.batchRow}
                    onClick={() => setBatchDrawer({ open: true, batch })}
                  >
                    <Typography className={classes.batchLink}>{batch.vendorBatchNumber}</Typography>
                    <OpenInNewIcon style={{ fontSize: 16, color: '#E53935' }} />
                  </Box>
                ))
              )}

              <hr style={{ margin: '1.5rem 0', borderColor: '#E0E0E0' }} />

              {/* ── Consignee GRN Details ── */}
              <SectionHeader title="Consignee GRN Details" classes={classes} />
              <Grid item xs={12} lg={11} style={{ paddingBottom: '1rem' }}>
                <TableContainer component={Paper} className={classes.tableContainer}>
                  <Table>
                    <TableHead>
                      <TableRow className={classes.tableHeader}>
                        <TableCell className={classes.headerCell}>Batch Number</TableCell>
                        <TableCell className={classes.headerCell}>Consignee GRN Number</TableCell>
                        <TableCell className={classes.headerCell}>Consignee GRN Generation Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {grnBatches.length > 0 ? (
                        grnBatches.map((batch) => (
                          <TableRow key={batch.schrockenDeliveryScheduleBatchID} className={classes.tableRow}>
                            <TableCell className={classes.tableCell}>{batch.vendorBatchNumber || "-"}</TableCell>
                            <TableCell className={classes.tableCell}>{batch.consigneeGRNNumber || "-"}</TableCell>
                            <TableCell className={classes.tableCell}>{formatDate(batch.consigneeGRNGeneratedDate) || "-" }</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow className={classes.tableRow}>
                          <TableCell className={classes.tableCell} colSpan={3} style={{ textAlign: 'center', color: '#999' }}>
                            No data available
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>

              <hr style={{ margin: '1.5rem 0', borderColor: '#E0E0E0' }} />

              {/* ── Shipment Events ── */}
              <SectionHeader title="Shipment Events" classes={classes} />
              <Box style={{ paddingLeft: 4 }}>
                {shipmentEvents.length > 0 ? shipmentEvents.map((evt, idx) => (
                  <Box key={idx} style={{ display: 'flex', gap: 14, marginBottom: 0 }}>
                    <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 18, flexShrink: 0 }}>
                      <Box className={classes.eventsDot} />
                      {idx < shipmentEvents.length - 1 && <Box className={classes.eventsLine} />}
                    </Box>
                    <Box style={{ paddingBottom: 20 }}>
                      <Typography className={classes.eventName}>{evt.event}</Typography>
                      <Typography className={classes.eventMeta}>{evt.date}</Typography>
                      <Typography className={classes.eventMeta}>{evt.time}</Typography>
                    </Box>
                  </Box>
                )) : (
                  <Typography style={{ fontSize: '0.9rem', color: '#888', paddingBottom: 12 }}>No data available</Typography>
                )}
              </Box>
              <hr style={{ margin: '1.5rem 0', borderColor: '#E0E0E0' }} />

              {/* ── Shipment Container Details ── */}
              <SectionHeader title="Shipment Container Details" classes={classes} />
              <Grid item xs={12} lg={11} style={{ paddingBottom: '1rem' }}>
                <TableContainer component={Paper} className={classes.tableContainer} style={{ maxWidth: '100%' }}>
                  <Table>
                    <TableHead>
                      <TableRow className={classes.tableHeader}>
                        <TableCell className={classes.headerCell}>FG Batch Number</TableCell>
                        <TableCell className={classes.headerCell}>Sponsor Product Code / Description</TableCell>
                        <TableCell className={classes.headerCell}>Quantity</TableCell>
                        <TableCell className={classes.headerCell}>Number of Pallets</TableCell>
                        <TableCell className={classes.headerCell}>Net Weight</TableCell>
                        <TableCell className={classes.headerCell}>Gross Weight</TableCell>
                        <TableCell className={classes.headerCell}>UoM</TableCell>
                        <TableCell className={classes.headerCell}>Temperature</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {batchesList.length > 0 ? (
                        batchesList.map((batch) => (
                          <TableRow key={batch.schrockenDeliveryScheduleBatchID} className={classes.tableRow}>
                            <TableCell className={classes.tableCell}>
                              <Box
                                className={classes.batchRow}
                                style={{ marginBottom: 0 }}
                                onClick={() => setBatchDrawer({ open: true, batch })}
                              >
                                <Typography className={classes.batchLink}>{batch.vendorBatchNumber || '-'}</Typography>
                                <OpenInNewIcon style={{ fontSize: 14, color: '#E53935' }} />
                              </Box>
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                              {batch.fgMaterialCode && batch.fgMaterialName
                                ? `${batch.fgMaterialCode} / ${batch.fgMaterialName}`
                                : batch.fgMaterialName || batch.fgMaterialCode || '-'}
                            </TableCell>
                            <TableCell className={classes.tableCell}>{batch.readyForDispatchQuantity ?? '-'}</TableCell>
                            <TableCell className={classes.tableCell}>{batch.noOfPallets || '-'}</TableCell>
                            <TableCell className={classes.tableCell}>{batch.netWeight || '-'}</TableCell>
                            <TableCell className={classes.tableCell}>{batch.grossWeight || '-'}</TableCell>
                            <TableCell className={classes.tableCell}>{batch.uomQA || batch.dispatchUOM || '-'}</TableCell>
                            <TableCell className={classes.tableCell}>{batch.tempRangeAndSetPoint || '-'}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow className={classes.tableRow}>
                          <TableCell className={classes.tableCell} colSpan={8} style={{ textAlign: 'center', color: '#999' }}>
                            No data available
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>

              <hr style={{ margin: '1.5rem 0', borderColor: '#E0E0E0' }} />
              {/* ── Sensor Data (decorative) ── */}
              <SectionHeader title="Sensor Data" classes={classes} />
              <Grid item xs={12} lg={11} style={{ paddingBottom: '1rem' }}>
                <TableContainer component={Paper} className={classes.tableContainer}>
                  <Table>
                    <TableHead>
                      <TableRow className={classes.tableHeader}>
                        <TableCell className={classes.headerCell} style={{ width: 130 }} />
                        <TableCell className={classes.headerCell}>Parameters&apos; Values</TableCell>
                        <TableCell className={classes.headerCell}>Time Stamp</TableCell>
                        <TableCell className={classes.headerCell}>Sensor Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {MOCK_SENSOR_DATA.map((sensor) => (
                        <TableRow key={sensor.label} className={classes.tableRow}>
                          <TableCell className={classes.boldCell}>{sensor.label}</TableCell>
                          <TableCell className={classes.tableCell}>
                            {sensor.label === 'Temperature' && row.temperature
                              ? row.temperature
                              : sensor.value}
                          </TableCell>
                          <TableCell className={classes.tableCell}>{sensor.timestamp}</TableCell>
                          <TableCell className={classes.linkCell}>Click here</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>

              <hr style={{ margin: '1.5rem 0', borderColor: '#E0E0E0' }} />

              {/* ── Shipment Profile ── */}
              <SectionHeader title="Shipment Profile" classes={classes} />
              <ShipmentProfileChart classes={classes} />

            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* ── Batch QuickView Drawer ── */}
      <SwipeableDrawer
        anchor="right"
        open={batchDrawer.open}
        onClose={() => setBatchDrawer({ open: false, batch: null })}
        onOpen={() => { }}
        classes={{ paper: classes.drawerPaper }}
      >
        {batchDrawer.open && batchDrawer.batch && (
          <QuickView
            setDialogOpen={() => setBatchDrawer({ open: false, batch: null })}
            schrockenDeliveryScheduleBatchID={batchDrawer.batch.schrockenDeliveryScheduleBatchID}
            batchRowData={batchDrawer.batch}
            isFromLogisticsDashboard={true}
            targetQuantity={batchDrawer.batch.readyForDispatchQuantity}
            poQuantity={batchDrawer.batch.readyForDispatchQuantity}
          />
        )}
      </SwipeableDrawer>
    </div>
  );
};

export default ShipmentDetailsPage;
